import { startOfMonth } from "date-fns";

import { KnownAction } from "../actions/events.actions";
import { IEventState } from "../models/events.interface";
import { Event, Presenter, Session } from "../../api";

const initialState: IEventState = {
  eventsByUid: {},
  presentersById: {},
  sessionsById: {},
  sessionsByEventId: {},
  presentersBySessionId: {},
  scoresByEventUid: {},
  downloadUrlByEventUid: {},
  diary: {
    date: startOfMonth(new Date()),
    eventUids: {},
  },
  period: "yearToDate",
  statisticsByPeriod: {},
  chartsByPeriod: {},
  eventUidsByPeriod: {},
  eventTypesByPeriod: {},
  viewsByEventUid: {},
  partnerAliasByEventUid: {},
};

function eventReducer(
  state: IEventState = initialState,
  action: KnownAction
): IEventState {
  switch (action.type) {
    case "REQUEST_STATISTICS_FOR_PERIOD": {
      return {
        ...state,
        statisticsByPeriod: {
          ...state.statisticsByPeriod,
          [action.period]: "loading",
        },
      };
    }
    case "SET_STATISTICS_FOR_PERIOD": {
      return {
        ...state,
        statisticsByPeriod: {
          ...state.statisticsByPeriod,
          [action.period]: action.data,
        },
      };
    }
    case "SET_STATISTICS_FOR_PERIOD_ERROR": {
      return {
        ...state,
        statisticsByPeriod: {
          ...state.statisticsByPeriod,
          [action.period]: "error",
        },
      };
    }
    case "REQUEST_FILTERED_STATISTICS": {
      return {
        ...state,
        filteredStatistics: "loading",
      };
    }
    case "SET_FILTERED_STATISTICS": {
      return {
        ...state,
        filteredStatistics: action.data,
      };
    }
    case "SET_FILTERED_STATISTICS_ERROR": {
      return {
        ...state,
        filteredStatistics: "error",
      };
    }
    case "REQUEST_CHARTS_FOR_PERIOD": {
      return {
        ...state,
        chartsByPeriod: {
          ...state.chartsByPeriod,
          [action.period]: "loading",
        },
      };
    }
    case "SET_CHARTS_FOR_PERIOD": {
      return {
        ...state,
        chartsByPeriod: {
          ...state.chartsByPeriod,
          [action.period]: action.data,
        },
      };
    }
    case "SET_CHARTS_FOR_PERIOD_ERROR": {
      return {
        ...state,
        chartsByPeriod: {
          ...state.chartsByPeriod,
          [action.period]: "error",
        },
      };
    }
    case "REQUEST_EVENTS_FOR_PERIOD": {
      return {
        ...state,
        eventUidsByPeriod: {
          ...state.eventUidsByPeriod,
          [action.period]: "loading",
        },
        eventTypesByPeriod: {
          ...state.eventTypesByPeriod,
          [action.period]: [],
        },
        filteredEventUids: void 0,
      };
    }
    case "SET_EVENTS_FOR_PERIOD": {
      const ids: string[] = [];
      const typesDictionary: Record<string, undefined> = {};
      action.events.forEach(x => {
        ids.push(x.id);
        if (x.type) {
          typesDictionary[x.type] = void 0;
        }
      });
      const types = Object.keys(typesDictionary).sort();
      return {
        ...state,
        eventUidsByPeriod: {
          ...state.eventUidsByPeriod,
          [action.period]: ids,
        },
        eventTypesByPeriod: {
          ...state.eventTypesByPeriod,
          [action.period]: types,
        },
      };
    }
    case "SET_EVENTS_FOR_PERIOD_ERROR": {
      return {
        ...state,
        eventUidsByPeriod: {
          ...state.eventUidsByPeriod,
          [action.period]: "error",
        },
      };
    }
    case "REQUEST_FILTERED_EVENTS": {
      return {
        ...state,
        filteredEventUids: "loading",
      };
    }
    case "SET_FILTERED_EVENTS": {
      return {
        ...state,
        filteredEventUids: action.ids,
      };
    }
    case "SET_FILTERED_EVENTS_ERROR": {
      return {
        ...state,
        filteredEventUids: "error",
      };
    }
    case "REQUEST_DIARY_EVENTS": {
      return {
        ...state,
        diary: {
          ...state.diary,
          eventUids: {
            ...state.diary.eventUids,
            [action.key]: "loading",
          },
        },
      };
    }
    case "SET_DIARY_EVENTS": {
      return {
        ...state,
        diary: {
          ...state.diary,
          eventUids: {
            ...state.diary.eventUids,
            [action.key]: action.ids,
          },
        },
      };
    }
    case "SET_DIARY_EVENTS_ERROR": {
      return {
        ...state,
        diary: {
          ...state.diary,
          eventUids: {
            ...state.diary.eventUids,
            [action.key]: "error",
          },
        },
      };
    }
    case "REQUEST_EVENT_DETAILS": {
      return {
        ...state,
        sessionsByEventId: {
          ...state.sessionsByEventId,
          [action.eventId]: "loading",
        },
      };
    }
    case "SET_EVENT_DETAILS_ERROR": {
      return {
        ...state,
        sessionsByEventId: {
          ...state.sessionsByEventId,
          [action.eventId]: "error",
        },
      };
    }
    case "SET_EVENT_DETAILS": {
      const sessionsPresenters: { [key: string]: string[] } = {};
      action.sessionsPresenters.forEach(item => {
        if (item.sessionId in sessionsPresenters) {
          sessionsPresenters[item.sessionId].push(item.presenterId);
        }
        else {
          sessionsPresenters[item.sessionId] = [item.presenterId];
        }
      });
      return {
        ...state,
        presentersById: {
          ...state.presentersById,
          ...action.presenters.reduce((result, item) => {
            result[item.id] = item;
            return result;
          }, {} as { [key: string]: Presenter }),
        },
        sessionsById: {
          ...state.sessionsById,
          ...action.sessions.reduce((result, item) => {
            result[item.id] = item;
            return result;
          }, {} as { [key: string]: Session }),
        },
        sessionsByEventId: {
          ...state.sessionsByEventId,
          [action.eventId]: action.sessions.map(x => x.id),
        },
        presentersBySessionId: {
          ...state.presentersBySessionId,
          ...sessionsPresenters,
        },
      };
    }
    case "REQUEST_EVENT_DOCUMENT": {
      const key = `${action.eventId}-${action.ticketId}`;
      return {
        ...state,
        downloadUrlByEventUid: {
          ...state.downloadUrlByEventUid,
          [key]: "loading",
        },
      };
    }
    case "SET_EVENT_DOCUMENT_ERROR": {
      const key = `${action.eventId}-${action.ticketId}`;
      return {
        ...state,
        downloadUrlByEventUid: {
          ...state.downloadUrlByEventUid,
          [key]: "error",
        },
      };
    }
    case "SET_EVENT_DOCUMENT": {
      const key = `${action.eventId}-${action.ticketId}`;
      return {
        ...state,
        downloadUrlByEventUid: {
          ...state.downloadUrlByEventUid,
          [key]: action.downloadUrl ?? null,
        },
      };
    }
    case "REQUEST_EVENT_SCORES": {
      return {
        ...state,
        scoresByEventUid: {
          ...state.scoresByEventUid,
          [action.eventUid]: "loading",
        },
      };
    }
    case "SET_EVENT_SCORES_ERROR": {
      return {
        ...state,
        scoresByEventUid: {
          ...state.scoresByEventUid,
          [action.eventUid]: "error",
        },
      };
    }
    case "SET_EVENT_SCORES": {
      return {
        ...state,
        scoresByEventUid: {
          ...state.scoresByEventUid,
          [action.eventUid]: action.scores ?? null,
        },
        partnerAliasByEventUid: {
          ...state.partnerAliasByEventUid,
          ...(action.currentPartnerAlias ? { [action.eventUid]: action.currentPartnerAlias } : {})
        },
      };
    }
    case "REQUEST_EVENT_VIEWS": {
      const key = `${action.eventId}-${action.ticketId}`;
      return {
        ...state,
        viewsByEventUid: {
          ...state.viewsByEventUid,
          [key]: "loading",
        },
      };
    }
    case "SET_EVENT_VIEWS_ERROR": {
      const key = `${action.eventId}-${action.ticketId}`;
      return {
        ...state,
        viewsByEventUid: {
          ...state.viewsByEventUid,
          [key]: "error",
        },
      };
    }
    case "SET_EVENT_VIEWS": {
      const key = `${action.eventId}-${action.ticketId}`;
      return {
        ...state,
        viewsByEventUid: {
          ...state.viewsByEventUid,
          [key]: action.data ?? [],
        },
      };
    }
    case "SET_EVENTS_PERIOD": {
      return {
        ...state,
        period: action.period,
      };
    }
    case "SET_DIARY_DATE": {
      return {
        ...state,
        diary: {
          ...state.diary,
          date: action.date,
        },
      };
    }
    case "CLEAR_FILTERS": {
      return {
        ...state,
        filteredEventUids: void 0,
      };
    }
    case "ADD_EVENTS": {
      return {
        ...state,
        eventsByUid: {
          ...state.eventsByUid,
          ...action.events.reduce((result, item) => {
            result[item.id] = item;
            return result;
          }, {} as { [key: string]: Event }),
        },
      };
    }
    case "ADD_PRESENTERS": {
      return {
        ...state,
        presentersById: {
          ...state.presentersById,
          ...action.presenters.reduce((result, item) => {
            result[item.id] = item;
            return result;
          }, {} as { [key: string]: Presenter }),
        },
      };
    }
    case "ADD_SESSIONS": {
      return {
        ...state,
        sessionsById: {
          ...state.sessionsById,
          ...action.sessions.reduce((result, item) => {
            result[item.id] = item;
            return result;
          }, {} as { [key: string]: Session }),
        },
      };
    }
    default:
      return state;
  }
}

export default eventReducer;
